import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PortalShell } from "@/components/layout/PortalShell";
import { STAFF_NAV } from "@/lib/staff-nav";
import { OperationsBanner } from "@/components/staff/OperationsBanner";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type Stage = Database["public"]["Enums"]["lead_stage"];
const STAGES: Stage[] = ["new", "contacted", "qualified", "booked", "converted", "lost"];

interface Lead {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  stage: Stage;
  source: string | null;
  created_at: string;
}

export const Route = createFileRoute("/staff/leads")({
  head: () => ({
    meta: [
      { title: "Leads Queue — Operations" },
      { name: "description", content: "Route and schedule inbound leads." },
      { property: "og:title", content: "Leads Queue — Operations" },
      { property: "og:description", content: "Route and schedule inbound leads." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: StaffLeads,
});

function StaffLeads() {
  const [rows, setRows] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data } = await supabase
      .from("leads")
      .select("id, full_name, email, phone, stage, source, created_at")
      .order("created_at", { ascending: false });
    setRows((data as Lead[]) ?? []);
    setLoading(false);
  };
  useEffect(() => { void load(); }, []);

  const setStage = async (id: string, stage: Stage) => {
    const { error } = await supabase.from("leads").update({ stage }).eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success("Stage updated");
    setRows((r) => r.map((l) => (l.id === id ? { ...l, stage } : l)));
  };

  return (
    <ProtectedRoute requiredRole="staff">
      <PortalShell title="Leads Queue" nav={STAFF_NAV}>
        <OperationsBanner />
        <Eyebrow>Routing</Eyebrow>
        <h1 className="mt-3 font-serif text-3xl font-semibold text-navy-900">Leads queue</h1>
        <p className="mt-2 font-sans text-sm text-charcoal-500">
          Update contact status and stage. Assignment to a licensed strategist is handled by an administrator.
        </p>
        <div className="mt-6 overflow-x-auto rounded-sm border border-bronze-600/30 bg-white">
          <table className="w-full min-w-[640px] text-left font-sans text-sm">
            <thead className="border-b border-bronze-600/30 text-[11px] uppercase tracking-[0.14em] text-charcoal-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Stage</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td className="px-4 py-6 text-charcoal-500" colSpan={4}>Loading…</td></tr>)}
              {!loading && rows.length === 0 && (<tr><td className="px-4 py-6 text-charcoal-500" colSpan={4}>No leads in the queue.</td></tr>)}
              {rows.map((l) => (
                <tr key={l.id} className="border-b border-bronze-600/15 last:border-0">
                  <td className="px-4 py-3 text-charcoal-900">{l.full_name}</td>
                  <td className="px-4 py-3 text-charcoal-500">{l.email ?? "—"}<br />{l.phone ?? ""}</td>
                  <td className="px-4 py-3 text-charcoal-500">{l.source ?? "—"}</td>
                  <td className="px-4 py-3">
                    <select
                      aria-label={`Stage for ${l.full_name}`}
                      className="min-h-11 rounded-sm border border-bronze-600/40 bg-cream-50 px-2 text-base text-charcoal-900"
                      value={l.stage}
                      onChange={(e) => void setStage(l.id, e.target.value as Stage)}
                    >
                      {STAGES.map((s) => (<option key={s} value={s}>{s}</option>))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </PortalShell>
    </ProtectedRoute>
  );
}
